import React, { useRef, useCallback } from 'react';
import { Star, PlusCircle, MinusCircle } from 'lucide-react';
import { colorFor, formatHeatPct, formatColumnPct } from '../../theme/mobileTokens';
import { getSimpleStrikePositionLabel } from '../../utils/presetMappings';
import { formatScenarioLabel } from '../../utils/elasticityCalculations';
import '../../styles/MobileHeatmapGrid.css';

/**
 * MobileHeatmapGrid — sticky-column heatmap matrix for mobile.
 *
 * Layout:
 *   ┌──────────┬─────────── synced horizontal scroll ────────┐
 *   │  STRIKE  │  -20%  -10%  -5%  +5%  +10%  +15%  +20% ... │  ← sticky header row
 *   │ $550C ⋮  │  -99.7  -95.2  -86.1  -24.1  +49.8 ...      │  ← contract row
 *   │ $540C ⋮  │  ...                                        │
 *   └──────────┴─────────────────────────────────────────────┘
 *
 * Sticky left column: 96px (strike, OTM/ITM chip, premium, kebab).
 * Each cell strip is its own scrollable container — they sync via
 * a shared ref array + a requestAnimationFrame-guarded onScroll handler.
 * Kebab opens StrikeKebabSheet (passed via onOpenKebab callback).
 *
 * Props:
 *   rows              — sortedData array from ElasticityHeatmapView (each has _heatmap[pct])
 *   scenarioArray     — sorted array of underlying-pct columns (e.g. [-20,-10,-5,5,10,15,...])
 *   displayMode       — 'premiumGain' | 'multiplier'
 *   onOpenKebab(row)  — called when the row's kebab is tapped
 *   onCellTap(row, scenarioPct)
 *                     — optional; called when the user taps a heatmap cell with
 *                       data (cells displaying `--` are tap no-ops)
 */
export default function MobileHeatmapGrid({
  rows,
  scenarioArray,
  displayMode,
  onOpenKebab,
  // Per-row action props — wire ★ ➕ ➖ in the new leftmost actions column.
  // All optional; the column degrades gracefully if any are absent.
  favoritedRowKeys,
  onToggleFavorite,
  onSaveToWatchlist,
  onRemoveRow,
  savingRowKey,
  onCellTap,
}) {
  // Refs for synchronized horizontal scrolling. Index 0 is the header row;
  // index i (>= 1) is the row at rows[i - 1].
  const stripRefs = useRef([]);
  const isSyncingRef = useRef(false);

  const registerStripRef = useCallback((idx, el) => {
    stripRefs.current[idx] = el;
  }, []);

  const handleStripScroll = useCallback((sourceIdx, scrollLeft) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    requestAnimationFrame(() => {
      const strips = stripRefs.current;
      for (let i = 0; i < strips.length; i++) {
        const el = strips[i];
        if (el && i !== sourceIdx && el.scrollLeft !== scrollLeft) {
          el.scrollLeft = scrollLeft;
        }
      }
      isSyncingRef.current = false;
    });
  }, []);

  if (!Array.isArray(rows) || rows.length === 0) return null;

  const formatStrike = (s, optionType) => {
    const num = parseFloat(s);
    const suffix = optionType?.toLowerCase() === 'put' ? 'P' : 'C';
    if (isNaN(num)) return `$${s}${suffix}`;
    return num % 1 === 0 ? `$${num.toFixed(0)}${suffix}` : `$${num.toFixed(2)}${suffix}`;
  };

  return (
    <div className="mhg-root">
      {/* Header row: sticky-top, sticky-left strike column + scrollable col labels */}
      <div className="mhg-header-row">
        {/* Matching empty cell for the actions column below */}
        <div className="mhg-actions-col mhg-actions-col-header" aria-hidden="true" />
        <div className="mhg-strike-col" style={{ cursor: 'default' }}>
          <span className="mhg-strike-col-label-line1">Strike</span>
          <span className="mhg-strike-col-label-line2">Underlying →</span>
        </div>
        <div
          className="mhg-strip"
          data-strip-idx={0}
          ref={(el) => registerStripRef(0, el)}
          onScroll={(e) => handleStripScroll(0, e.currentTarget.scrollLeft)}
        >
          {scenarioArray.map((pct) => (
            <div key={pct} className="mhg-col-header">
              {formatColumnPct(pct)}
            </div>
          ))}
        </div>
      </div>

      {/* Contract rows */}
      {rows.map((row, idx) => {
        const stripIdx = idx + 1; // 0 is the header row
        const optionType = row['Option Type'];
        const positionType = row['Position Type'];
        const strikePos = row['Strike Position'];
        const strikePosLabel = strikePos !== undefined && strikePos !== null
          ? getSimpleStrikePositionLabel(strikePos, optionType)
          : null;
        // Compact inline OTM/ITM label that sits next to the strike value:
        //   ATM      → 'ATM'
        //   OTM/ITM  → '(+25%)' / '(-25%)' (parens only, no OTM/ITM prefix)
        const inlineStrikePct = (() => {
          if (!strikePosLabel) return null;
          if (strikePosLabel.startsWith('ATM')) return 'ATM';
          const m = strikePosLabel.match(/\(([^)]+)\)/);
          return m ? `(${m[1]})` : null;
        })();
        const premium = parseFloat(row['Current Premium']);
        const rowKey = `${row['Ticker']}|${row['Option Type']}|${row['Strike']}|${row['Expiration']}`;
        const isFavorited = favoritedRowKeys ? favoritedRowKeys.has(rowKey) : false;
        const isCurrentlySaving = savingRowKey === rowKey;

        return (
          <div key={rowKey} className="mhg-row">
            {/* Actions column — favorite / save-to-watchlist / drop */}
            <div className="mhg-actions-col">
              <button
                type="button"
                className={`mhg-action-btn ${isFavorited ? 'mhg-action-btn-favorited' : ''}`}
                onClick={(e) => { e.stopPropagation(); onToggleFavorite && onToggleFavorite(row); }}
                aria-label={isFavorited ? 'Unfavorite contract' : 'Favorite contract'}
                title={isFavorited ? 'Unfavorite' : 'Favorite'}
              >
                <Star size={12} fill={isFavorited ? 'currentColor' : 'none'} />
              </button>
              <button
                type="button"
                className="mhg-action-btn"
                onClick={(e) => { e.stopPropagation(); onSaveToWatchlist && onSaveToWatchlist(row); }}
                disabled={isCurrentlySaving}
                aria-label="Save to watchlist"
                title="Save to Watchlist"
              >
                {isCurrentlySaving
                  ? <span className="mhg-action-spinner" />
                  : <PlusCircle size={12} />}
              </button>
              <button
                type="button"
                className="mhg-action-btn mhg-action-btn-remove"
                onClick={(e) => { e.stopPropagation(); onRemoveRow && onRemoveRow(row); }}
                aria-label="Remove contract from results"
                title="Remove from results"
              >
                <MinusCircle size={12} />
              </button>
            </div>

            {/* Sticky-left strike column — tapping anywhere on the column
                opens the chart-detail modal (same target as the previous
                kebab affordance) */}
            <div
              className="mhg-strike-col"
              role="button"
              tabIndex={0}
              onClick={() => onOpenKebab && onOpenKebab(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (onOpenKebab) onOpenKebab(row);
                }
              }}
              aria-label={`Open ${row['Ticker']} ${row['Strike']} details`}
            >
              {/* Header: ticker + position type */}
              <div className="mhg-strike-head">
                <span className="mhg-strike-ticker">{row['Ticker']}</span>
                {positionType && (
                  <span className="mhg-strike-position">{String(positionType).toUpperCase()}</span>
                )}
              </div>

              {/* Strike value + inline OTM/ITM percent */}
              <div className="mhg-strike-strike">
                <span className="mhg-strike-value">{formatStrike(row['Strike'], optionType)}</span>
                {inlineStrikePct && (
                  <span className="mhg-strike-pct">{inlineStrikePct}</span>
                )}
              </div>

              {/* Expiration (above premium per design) */}
              {row['Expiration'] && (
                <span className="mhg-strike-exp" title={row['Expiration']}>
                  Exp {row['Expiration']}
                </span>
              )}

              {/* Premium (below expiration) */}
              {!isNaN(premium) && (
                <span className="mhg-strike-prem">
                  <span className="mhg-strike-prem-label">Prem </span>
                  <span className="mhg-strike-prem-value">${premium.toFixed(2)}</span>
                </span>
              )}
            </div>

            {/* Scrollable cell strip */}
            <div
              className="mhg-strip"
              data-strip-idx={stripIdx}
              ref={(el) => registerStripRef(stripIdx, el)}
              onScroll={(e) => handleStripScroll(stripIdx, e.currentTarget.scrollLeft)}
            >
              {scenarioArray.map((pct) => {
                const cell = row._heatmap?.[pct];
                const raw = displayMode === 'multiplier' ? cell?.multiplier : cell?.premiumGain;
                // For multiplier mode, color the cell by a synthesized %-equivalent
                // (multiplier × scenario%) so the same heat ramp applies.
                const colorInput = displayMode === 'multiplier'
                  ? (raw != null && !isNaN(raw) ? raw * Math.abs(pct) : raw)
                  : raw;
                const { bg, fg } = colorFor(colorInput);
                const display = displayMode === 'multiplier'
                  ? (raw == null || isNaN(raw) || raw <= 0 ? '--' : `${Number(raw).toFixed(1)}x`)
                  : formatHeatPct(raw);

                // Tap is enabled when we have a real premiumGain to show in the
                // detail sheet — multiplier-mode cells with no data still get
                // a tap if their underlying premiumGain is finite.
                const hasData = cell?.premiumGain != null && !isNaN(cell.premiumGain);
                const handleTap = hasData && onCellTap
                  ? () => onCellTap(row, pct)
                  : undefined;

                return (
                  <div
                    key={pct}
                    className="mhg-cell"
                    role={handleTap ? 'button' : undefined}
                    tabIndex={handleTap ? 0 : undefined}
                    onClick={handleTap}
                    onKeyDown={handleTap ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTap();
                      }
                    } : undefined}
                    aria-label={handleTap
                      ? `${row['Ticker']} ${row['Strike']}${row['Option Type']?.toLowerCase() === 'put' ? 'P' : 'C'} at ${formatColumnPct(pct)} underlying scenario`
                      : undefined}
                    style={{ backgroundColor: bg, color: fg, cursor: handleTap ? 'pointer' : 'default' }}
                  >
                    <div className="mhg-cell-top">{formatColumnPct(pct)}</div>
                    <div className="mhg-cell-bottom">{display}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Re-export for ergonomic reuse from sibling files
export { formatScenarioLabel };
